import React, { useState } from 'react';
import axios from 'axios';
import { useDispatch, sendFlashMessage } from '@/shared/components/flash';
import ApiErrorMessage from '@/shared/components/ApiErrorMessage';
import ConfirmDeleteModal from './ConfirmDeleteModal';

const CategoryKeywords = ({ organizationId, categoryId, initialKeywords }) => {
    const dispatch = useDispatch();

    const [keywords, setKeywords] = useState(initialKeywords || []);
    const [newKeyword, setNewKeyword] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [saving, setSaving] = useState(false);

    const onError = (error) => {
        dispatch(sendFlashMessage(<ApiErrorMessage errors={error.response?.data} />, 'alert-danger'));
    };

    const onAddKeyword = () => {
        if (!newKeyword.trim()) return;
        setSaving(true);
        axios.post(`/organization/${organizationId}/category/${categoryId}/keyword`, { keyword: newKeyword })
            .then(res => {
                setKeywords(prev => [...prev, res.data.keyword]);
                setNewKeyword('');
            })
            .catch(onError)
            .finally(() => setSaving(false));
    };

    const onStartEdit = (keyword) => {
        setEditingId(keyword.id);
        setEditText(keyword.keyword);
    };

    const onCancelEdit = () => {
        setEditingId(null);
        setEditText('');
    };

    const onSaveEdit = (keyword) => {
        if (!editText.trim()) return;
        setSaving(true);
        axios.put(`/organization/${organizationId}/category/${categoryId}/keyword/${keyword.id}`, { keyword: editText })
            .then(res => {
                setKeywords(prev => prev.map(k => (k.id === keyword.id ? res.data.keyword : k)));
                setEditingId(null);
                setEditText('');
            })
            .catch(onError)
            .finally(() => setSaving(false));
    };

    const onConfirmDelete = () => {
        if (!deleteTarget) return;
        axios.delete(`/organization/${organizationId}/category/${categoryId}/keyword/${deleteTarget.id}`)
            .then(() => {
                setKeywords(prev => prev.filter(k => k.id !== deleteTarget.id));
                setDeleteTarget(null);
            })
            .catch(error => {
                setDeleteTarget(null);
                onError(error);
            });
    };

    return (
        <div className="form__form-group">
            <span className="form__form-group-label">Keywords</span>
            <div className="form__form-group-field">
                <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                    {keywords.map(keyword => (
                        <li key={keyword.id} style={{ marginBottom: '8px' }}>
                            {editingId === keyword.id ? (
                                <span>
                                    <input
                                        type="text"
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <a href="#save-keyword" onClick={(e) => { e.preventDefault(); onSaveEdit(keyword); }}>Save</a>
                                    <span style={{ width: '10px', display: 'inline-block' }} />
                                    <a href="#cancel-keyword" onClick={(e) => { e.preventDefault(); onCancelEdit(); }}>Cancel</a>
                                </span>
                            ) : (
                                <span>
                                    {keyword.keyword}
                                    <span style={{ width: '10px', display: 'inline-block' }} />
                                    <a href="#edit-keyword" onClick={(e) => { e.preventDefault(); onStartEdit(keyword); }}>Edit</a>
                                    <span style={{ width: '10px', display: 'inline-block' }} />
                                    <a
                                        href="#remove-keyword"
                                        className="text-danger"
                                        onClick={(e) => { e.preventDefault(); setDeleteTarget(keyword); }}
                                    >
                                        Remove
                                    </a>
                                </span>
                            )}
                        </li>
                    ))}
                    {keywords.length === 0 && <li>No keywords yet.</li>}
                </ul>
                <div className="form__form-group-row">
                    <input
                        type="text"
                        placeholder="Add keyword"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        style={{ marginRight: '8px' }}
                    />
                    <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={onAddKeyword}>
                        Add
                    </button>
                </div>
            </div>
            <ConfirmDeleteModal
                isOpen={!!deleteTarget}
                message={deleteTarget ? `Remove keyword "${deleteTarget.keyword}"?` : ''}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={onConfirmDelete}
            />
        </div>
    );
};

export default CategoryKeywords;
